import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import type { WaitingRoomPage } from '@/features/webinar/types';
import { useEffect, useState } from 'react';

type SurveyFormValues = {
  title: string;
  subtitle: string;
  questions: {
    id: number;
    question: string;
    answerType: '1' | '2';
    answers: string[];
  }[];
};

interface PreviewSurveyProps {
  form: UseFormReturn<SurveyFormValues>;
  visualIdentity: WorkspaceVisualIdentity;
  currentWaitingRoomPage?: WaitingRoomPage | null;
}

export default function PreviewSurvey({ form, visualIdentity }: PreviewSurveyProps) {
  const theme = visualIdentity?.theme;
  const { t } = useTranslation('webinars');
  const [surveyStep, setSurveyStep] = useState(1);
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [questionId: number]: string;
  }>({});

  const questions = form.watch('questions') || [];
  const currentQuestion = questions[surveyStep - 1];

  useEffect(() => {
    if (surveyStep > questions.length && questions.length > 0) {
      setSurveyStep(questions.length);
    }
  }, [questions.length, surveyStep]);

  return (
    <div className="flex flex-col   gap-4 w-full">
      <h3
        className="text-xl font-medium"
        style={{
          color: theme === 'DARK' ? 'white' : 'black',
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
        }}
      >
        {form.watch('title') || t('waitingRoom.previewPlaceholders.surveyTitle')}
      </h3>

      <p
        className="text-sm"
        style={{
          fontFamily: visualIdentity?.fontFamily || 'Poppins',
          color: theme === 'DARK' ? 'white' : '#6D6E7A',
        }}
      >
        {form.watch('subtitle') || t('waitingRoom.previewPlaceholders.surveySubtitle')}
      </p>

      <div className="flex flex-col gap-4">
        {questions.length > 0 && currentQuestion ? (
          <div>
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                {surveyStep > 1 && (
                  <ArrowLeftIcon
                    className="size-6 text-[#9E9FA6] cursor-pointer mr-2"
                    onClick={() => {
                      setSurveyStep(surveyStep - 1);
                    }}
                  />
                )}
                <h3
                  className="text-base font-medium"
                  style={{
                    color: theme === 'DARK' ? 'white' : 'black',
                    fontFamily: visualIdentity?.fontFamily || 'Poppins',
                  }}
                >
                  <span
                    style={{
                      color: theme === 'DARK' ? '#95B6FF' : '#2C6CFF',
                      fontFamily: visualIdentity?.fontFamily || 'Poppins',
                    }}
                  >
                    {surveyStep}/{questions.length}
                  </span>{' '}
                  {currentQuestion.question || t('waitingRoom.survey.untitledQuestion')}
                </h3>
              </div>
              {surveyStep < questions.length && (
                <ArrowRightIcon
                  className="size-6 text-[#9E9FA6] cursor-pointer"
                  onClick={() => {
                    setSurveyStep(surveyStep + 1);
                  }}
                />
              )}
            </div>
            <div className="flex flex-col gap-2 pt-4">
              {currentQuestion.answerType === '1' ? (
                <RadioGroup
                  value={selectedAnswers[currentQuestion.id] || ''}
                  onValueChange={(value) => {
                    setSelectedAnswers((prev) => ({
                      ...prev,
                      [currentQuestion.id]: value,
                    }));
                  }}
                  className="space-y-2"
                >
                  {currentQuestion.answers.map((answer, answerIndex) => (
                    <div
                      key={answerIndex}
                      className="flex items-center gap-2 p-2 rounded-lg"
                      style={{
                        backgroundColor:
                          theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <RadioGroupItem
                        value={answerIndex.toString()}
                        id={`${currentQuestion.id}-${answerIndex}`}
                      />
                      <label
                        htmlFor={`${currentQuestion.id}-${answerIndex}`}
                        className="text-base cursor-pointer flex-1"
                        style={{
                          color: theme === 'DARK' ? 'white' : 'black',
                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                        }}
                      >
                        {answer || t('waitingRoom.survey.emptyAnswer')}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
              ) : (
                <div className="space-y-2">
                  {currentQuestion.answers.map((answer, answerIndex) => (
                    <div
                      key={answerIndex}
                      className="flex items-center gap-2 p-2 rounded-lg"
                      style={{
                        backgroundColor:
                          theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <Checkbox className="cursor-pointer size-6" />
                      <p
                        className="text-base"
                        style={{
                          color: theme === 'DARK' ? 'white' : 'black',
                          fontFamily: visualIdentity?.fontFamily || 'Poppins',
                        }}
                      >
                        {answer || t('waitingRoom.survey.emptyAnswer')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p
              className="text-sm"
              style={{
                color: theme === 'DARK' ? '#6D6E7A' : '#6D6E7A',
                fontFamily: visualIdentity?.fontFamily || 'Poppins',
              }}
            >
              {t('waitingRoom.survey.noQuestions')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
